import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { API_URL_All } from '../../api';
import axiosInstance from '../../axiosInstance';
import { toast } from "react-toastify";
import * as general from '../../libs/general';
import { StarRating } from '../../components/tickets/Tickets';
import { RiDeleteBin6Line } from "react-icons/ri";
import { FiSave, FiX } from "react-icons/fi";
import { FaPlus } from "react-icons/fa";
import { fetchProductMenuPermissions } from "../../libs/customerCreatePermission";
import { groupMoney, unformatMoney } from "../../admin/accounting/utils/moneyFormat";
import ProductDrivePanel from "./ProductDrivePanel";

const defaultProperties = [];

const hasSizeDimensions = (item) => {
  const sizeX = Number(item?.sizeX ?? 0);
  const sizeY = Number(item?.sizeY ?? 0);
  const sizeZ = Number(item?.sizeZ ?? 0);
  return sizeX + sizeY + sizeZ > 0;
};

const createVariantDraft = (baseModel = {}, sortOrder = 0) => ({
  productId: baseModel.id ?? '',
  propertiesValue: '',
  code: baseModel.code ?? '',
  price: baseModel.price ?? '',
  priceDeclare: baseModel.priceDeclare ?? '',
  weight: baseModel.weight ?? '',
  size: baseModel.size ?? '',
  sizeX: baseModel.sizeX ?? '',
  sizeY: baseModel.sizeY ?? '',
  sizeZ: baseModel.sizeZ ?? '',
  size3: hasSizeDimensions(baseModel),
  taxCode: baseModel.taxCode ?? '',
  taxImport: baseModel.taxImport ?? '',
  taxVat: baseModel.taxVat ?? '',
  description: '',
  note: '',
  imagePath: '',
  image: null,
  imagePreviewUrl: '',
  active: true,
  sortOrder,
});

const mapVariantFromApi = (item = {}, baseModel = {}, sortOrder = 0) => ({
  id: item.id ?? '',
  productId: item.productId ?? baseModel.id ?? '',
  propertiesValue: item.propertiesValue ?? '',
  code: item.code ?? '',
  price: item.price ?? '',
  priceDeclare: item.priceDeclare ?? '',
  weight: item.weight ?? '',
  size: item.size ?? '',
  sizeX: item.sizeX ?? '',
  sizeY: item.sizeY ?? '',
  sizeZ: item.sizeZ ?? '',
  size3: hasSizeDimensions(item),
  taxCode: item.taxCode ?? '',
  taxImport: item.taxImport ?? '',
  taxVat: item.taxVat ?? '',
  description: item.description ?? '',
  note: item.note ?? '',
  imagePath: item.imagePath ?? '',
  image: null,
  imagePreviewUrl: '',
  active: item.active ?? true,
  sortOrder: item.sortOrder ?? sortOrder,
});

const appendFormValue = (formData, key, value) => {
  if (value === undefined || value === null || value === '') return;
  formData.set(key, value);
};

const revokeVariantPreview = (item) => {
  if (item?.imagePreviewUrl && item.imagePreviewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(item.imagePreviewUrl);
  }
};

const ProductEditor = ({ data, handleSearch }) => {
  const [show, setShow] = useState(true);
  const [isLocked, setIsLocked] = useState(true);
  const [model, setModel] = useState({});
  const [activeTab, setActiveTab] = useState('tab1');
  const [properties, setProperties] = useState(defaultProperties);
  const [hasVariants, setHasVariants] = useState(false);
  const [variantSizeMode, setVariantSizeMode] = useState('size');
  const variantImageInputRefs = useRef({});
  const savingRef = useRef(false);
  const [productFeaturePerm, setProductFeaturePerm] = useState({
    loaded: false,
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  });
  const [adminUsers, setAdminUsers] = useState([]);
  const currentUser = useSelector((state) => state.user.user);
  const viewerIsAdmin = currentUser?.isAdmin === true;
  const effectivePerm = {
    canView: data?.canView ?? productFeaturePerm.canView,
    canCreate: data?.canCreate ?? productFeaturePerm.canCreate,
    canEdit: data?.canEdit ?? productFeaturePerm.canEdit,
    canDelete: data?.canDelete ?? productFeaturePerm.canDelete,
  };
  const isSharedReadonlyForRegularUser = model?.productType === 'share' && model?.isAdmin !== true;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get("/api/users/searchAdminUsers", {
          params: { page: 0, size: 500, keyword: "" },
        });
        if (!cancelled) {
          setAdminUsers(response?.data?.page?.content || []);
        }
      } catch {
        if (!cancelled) setAdminUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchProductMenuPermissions(axiosInstance);
      if (cancelled) return;
      setProductFeaturePerm({
        loaded: true,
        canView: Boolean(p.canView),
        canCreate: Boolean(p.canCreate),
        canEdit: Boolean(p.canEdit),
        canDelete: Boolean(p.canDelete),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setActiveTab('tab1');
    fetchOneRecord(data.id);
  }, [data]);

  useEffect(() => {
    const form = document.querySelector('#product_editor_form');
    if (!form) return;

    const inputs = form.querySelectorAll('input');
    inputs.forEach((input) => {
      const type = String(input.type || 'text').toLowerCase();
      if (['checkbox', 'radio', 'file', 'button', 'submit'].includes(type)) {
        input.disabled = isLocked;
      } else {
        input.readOnly = isLocked;
        if (input.disabled) input.disabled = false;
      }
    });

    const textareas = form.querySelectorAll('textarea');
    textareas.forEach((textarea) => {
      textarea.readOnly = isLocked;
      if (textarea.disabled) textarea.disabled = false;
    });

    const selects = form.querySelectorAll('select');
    selects.forEach((select) => {
      select.disabled = isLocked;
    });
  }, [isLocked, activeTab, model?.id, properties.length]);

  useEffect(() => {
    if (!productFeaturePerm.loaded && data?.canEdit == null && data?.canCreate == null) return;
    const writableByPermission = model?.id ? effectivePerm.canEdit : effectivePerm.canCreate;
    setIsLocked(!writableByPermission || isSharedReadonlyForRegularUser);
  }, [productFeaturePerm, data?.canEdit, data?.canCreate, model?.id]);

  const handleActiveTab = async (tabName) => {
    setActiveTab(tabName);
  };

  const handleDeleteProduct = () => {
    general.openConfirm(async () => {
      try {
        const requestApi = `/api/products/deleteProduct?id=${model.id}`;
        const response = await axiosInstance.get(requestApi);

        if (!response.data.success) {
          toast.error(response.data.message);
          return;
        }
        toast.success(response.data.message);
        if (handleSearch) handleSearch();
        handleModalClose();
      } catch (error) {
        console.error(error);
        toast.error(error.message);
      }
    });
  };

  const handleModalClose = () => {
    properties.forEach(revokeVariantPreview);
    const allOldErrors = document.querySelectorAll('.error-msg');
    for (let i = allOldErrors.length - 1; i >= 0; i -= 1) allOldErrors[i].remove();
    document.activeElement.blur();
    setShow(false);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    const errorTag = e.currentTarget.querySelector('.error-msg');
    if (errorTag) return;
    savingRef.current = true;

    if (hasVariants) {
      if (properties.length === 0) {
        toast.error('Vui lòng thêm ít nhất 1 biến thể sản phẩm.');
        return;
      }

      const invalidVariant = properties.find((item) => !item?.propertiesValue?.trim());
      if (invalidVariant) {
        toast.error('Mỗi biến thể cần có tên thuộc tính, ví dụ: Size M / Màu đỏ.');
        return;
      }
    }

    const fileTag = document.querySelector('.img-preview input[type="file"]');
    const file = fileTag.files[0];
    const formData = new FormData();
    if (file) {
      formData.set('image', file);
    }

    const ignoreListKeys = ['listCustomers', 'listProviders', 'listOrders', 'properties', 'size3', 'careName'];
    Object.keys(model).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(model, key) && model[key] !== null && !ignoreListKeys.includes(key)) {
        formData.set(key, model[key]);
      }
    });

    const formCollectData = general.getFormData('#product_editor_form');
    formData.set("categoryNames", formCollectData.categoryNames);

    if (hasVariants) {
      properties.forEach((value, index) => {
        const normalizedItem = {
          ...value,
          productId: model.id ?? value.productId ?? '',
          propertiesValue: value.propertiesValue?.trim() ?? '',
          code: value.code?.trim() ?? '',
          taxCode: value.taxCode?.trim() ?? '',
          sortOrder: index,
          active: value.active ?? true,
        };

        if (!normalizedItem.propertiesValue) return;

        appendFormValue(formData, `properties[${index}].id`, normalizedItem.id);
        appendFormValue(formData, `properties[${index}].productId`, normalizedItem.productId);
        formData.set(`properties[${index}].propertiesValue`, normalizedItem.propertiesValue);
        appendFormValue(formData, `properties[${index}].code`, normalizedItem.code);
        appendFormValue(formData, `properties[${index}].price`, normalizedItem.price);
        appendFormValue(formData, `properties[${index}].priceDeclare`, normalizedItem.priceDeclare);
        appendFormValue(formData, `properties[${index}].weight`, normalizedItem.weight);
        appendFormValue(formData, `properties[${index}].size`, normalizedItem.size);
        appendFormValue(formData, `properties[${index}].sizeX`, normalizedItem.sizeX);
        appendFormValue(formData, `properties[${index}].sizeY`, normalizedItem.sizeY);
        appendFormValue(formData, `properties[${index}].sizeZ`, normalizedItem.sizeZ);
        appendFormValue(formData, `properties[${index}].taxCode`, normalizedItem.taxCode);
        appendFormValue(formData, `properties[${index}].taxImport`, normalizedItem.taxImport);
        appendFormValue(formData, `properties[${index}].taxVat`, normalizedItem.taxVat);
        appendFormValue(formData, `properties[${index}].description`, normalizedItem.description);
        appendFormValue(formData, `properties[${index}].note`, normalizedItem.note);
        appendFormValue(formData, `properties[${index}].imagePath`, normalizedItem.imagePath);
        if (normalizedItem.image) {
          formData.set(`properties[${index}].image`, normalizedItem.image);
        }
        formData.set(`properties[${index}].active`, normalizedItem.active);
        formData.set(`properties[${index}].sortOrder`, normalizedItem.sortOrder);
      });
    }

    let providerIndex = 0;
    (model.listProviders ?? []).forEach((provider) => {
      if (provider.providerName) {
        formData.set(`listProviders[${providerIndex}].providerId`, provider.providerId);
        formData.set(`listProviders[${providerIndex}].providerName`, provider.providerName);
        formData.set(`listProviders[${providerIndex}].productLink`, provider.productLink);
        providerIndex += 1;
      }
    });

    let customerIndex = 0;
    (model.listCustomers ?? []).forEach((item) => {
      if (item.customerCode) {
        formData.set(`listCustomers[${customerIndex}].id`, item.id);
        formData.set(`listCustomers[${customerIndex}].customerCode`, item.customerCode);
        customerIndex += 1;
      }
    });

    try {
      const requestApi = `/api/products/saveProduct`;
      const response = await axiosInstance.post(requestApi, formData);
      if (!response.data.success) {
        general.showFormError('.show .modal-body', response.data);
        return;
      }
      toast.success(response.data.message);
      if (handleSearch) handleSearch();
      handleModalClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      savingRef.current = false;
    }
  };

  const handleModalShow = () => {
    const params = { categoryNames: model.categoryNames };
    window.initializeFilterValue(params);
  };

  const fetchOneRecord = async (id) => {
    properties.forEach(revokeVariantPreview);
    setIsLocked(true);
    setModel({});
    setProperties(defaultProperties);
    setHasVariants(false);
    setVariantSizeMode('size');

    try {
      const urlApi = `/api/products/getProduct?id=${id}`;
      const response = await axiosInstance.get(urlApi);

      const obj = response.data.data;
      obj.size3 = hasSizeDimensions(obj);

      if (id === 0) {
        if (data.productType === 'share') {
          obj.productType = 'share';
          obj.providers = data.providerName;
        }
        const config = general.getLocalStorage("user_config");
        if (!obj.careId && config?.userId) {
          obj.careId = config.userId;
        }
      }

      const loadedProperties = Array.isArray(obj.properties)
        ? obj.properties.map((item, index) => mapVariantFromApi(item, obj, index))
        : defaultProperties;

      setModel(obj);
      setProperties(loadedProperties);
      setHasVariants(loadedProperties.length > 0);
      setVariantSizeMode(loadedProperties.some((item) => item.size3) ? 'size3' : 'size');

      const writableByPermission = obj.id ? effectivePerm.canEdit : effectivePerm.canCreate;
      const sharedReadonly = obj.productType === 'share' && obj.isAdmin !== true;
      setIsLocked(!writableByPermission || sharedReadonly);
      setShow(true);
    } catch (error) {
      console.error(error);
      setModel({ error: error.message });
      toast.error(error.message);
    }
  };

  const carePersonDisplayName =
    model.careName ||
    adminUsers.find((u) => Number(u.id) === Number(model.careId))?.name ||
    adminUsers.find((u) => Number(u.id) === Number(model.careId))?.username ||
    (model.careId ? `User #${model.careId}` : "—");
  const canEditCarePerson = viewerIsAdmin && !isLocked;

  const getPageTitle = (id, productType) => {
    let name = 'sản phẩm cá nhân';
    if (productType === 'share') name = 'sản phẩm dùng chung';
    if (id) return capitalizeFirstLetter(name);
    return `Thêm mới ${name}`;
  };

  const capitalizeFirstLetter = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const addCustomer = () => {
    const obj = { ...model };
    const details = obj.listCustomers;
    if (!details || details.length === 0) {
      obj.listCustomers = [{}];
    } else {
      obj.listCustomers.push({});
    }

    setModel(obj);
  };

  const addProvider = () => {
    const obj = { ...model };
    const details = obj.listProviders;
    const newProvider = {};
    if (!details || details.length === 0) {
      obj.listProviders = [newProvider];
    } else {
      obj.listProviders.push(newProvider);
    }

    setModel(obj);
  };

  const handleVariantToggle = (checked) => {
    setHasVariants(checked);
    if (checked && properties.length === 0) {
      setProperties([{
        ...createVariantDraft(model, 0),
        size3: variantSizeMode === 'size3',
      }]);
    }
  };

  const handleVariantSizeModeChange = (nextMode) => {
    setVariantSizeMode(nextMode);
    setProperties((prev) => prev.map((item, itemIndex) => {
      const nextItem = {
        ...item,
        size3: nextMode === 'size3',
        sortOrder: itemIndex,
      };

      if (nextMode === 'size3') {
        nextItem.size = '';
      } else {
        nextItem.sizeX = '';
        nextItem.sizeY = '';
        nextItem.sizeZ = '';
      }

      return nextItem;
    }));
  };

  const addVariant = () => {
    setHasVariants(true);
    setProperties((prev) => [
      ...prev,
      {
        ...createVariantDraft(model, prev.length),
        size3: variantSizeMode === 'size3',
      },
    ]);
  };

  const removeVariant = (index) => {
    setProperties((prev) => {
      const removedItem = prev[index];
      revokeVariantPreview(removedItem);
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const setVariantImage = (index, e) => {
    const file = e.target.files?.[0] ?? null;

    setProperties((prev) => prev.map((item, itemIndex) => {
      if (itemIndex !== index) return item;

      revokeVariantPreview(item);
      return {
        ...item,
        image: file,
        imagePreviewUrl: file ? URL.createObjectURL(file) : '',
      };
    }));

    e.target.value = '';
  };

  const clearVariantImage = (index) => {
    setProperties((prev) => prev.map((item, itemIndex) => {
      if (itemIndex !== index) return item;

      revokeVariantPreview(item);
      return {
        ...item,
        image: null,
        imagePath: '',
        imagePreviewUrl: '',
      };
    }));
  };

  const openVariantImagePicker = (index) => {
    if (isLocked) return;
    variantImageInputRefs.current[index]?.click();
  };

  const setVariantItem = (index, name, e) => {
    let value = e.target.value;
    if (value === 'on') value = e.target.checked;

    setProperties((prev) => prev.map((item, itemIndex) => {
      if (itemIndex !== index) return item;

      const nextItem = { ...item, [name]: value, sortOrder: itemIndex };

      if (name === 'size') {
        nextItem.sizeX = '';
        nextItem.sizeY = '';
        nextItem.sizeZ = '';
      }

      if (name === 'sizeX' || name === 'sizeY' || name === 'sizeZ') {
        nextItem.size = '';
      }

      return nextItem;
    }));
  };

  const setDetailsItem = async (index, name, e) => {
    let value = e.target.value;
    if (value === 'on') value = e.target.checked;
    const newDetails = [...model.listProviders];
    const item = newDetails[index];

    if (name === 'providerName') {
      const thTag = e.target.closest('.th');
      if (thTag) {
        const selecteTag = thTag.querySelector('.item_selected');
        if (value === '') item.name = '';
        if (selecteTag && selecteTag.innerText === value && value) {
          setIsLocked(true);
          item.name = '';
          try {
            const urlApi = `/api/providers/getProviderByName?name=${value}`;
            const response = await axiosInstance.get(urlApi);

            const obj = response.data.data;
            obj.providerId = obj.id;
            obj.providerName = obj.name;
            Object.assign(item, obj);
          } catch (error) {
            const message = error.response ? error.response.data.message : error.message;
            console.error(error);
            toast.error(message);
          }
          setIsLocked(false);
        }
      }
    }

    item[name] = value;
    setModel({ ...model, listProviders: newDetails });
  };

  const resolveProviderOnBlur = async (index) => {
    const item = model.listProviders && model.listProviders[index];
    if (!item || item.providerId || !item.providerName) return;
    const typedName = item.providerName;
    try {
      const urlApi = `/api/providers/getProviderByName?name=${encodeURIComponent(typedName)}`;
      const response = await axiosInstance.get(urlApi);
      const obj = response.data.data;
      if (obj && obj.id && obj.name && obj.name.trim().toLowerCase() === typedName.trim().toLowerCase()) {
        setModel((prev) => {
          const listProviders = [...(prev.listProviders || [])];
          const current = listProviders[index];
          if (!current || current.providerId || current.providerName !== typedName) return prev;
          listProviders[index] = { ...current, providerId: obj.id, providerName: obj.name };
          return { ...prev, listProviders };
        });
      }
    } catch (error) {
      // Auto-resolve tiện lợi - nếu NCC thật sự chưa tồn tại, backend sẽ báo lỗi rõ ràng khi Lưu.
      console.error(error);
    }
  };

  const setUserItem = async (index, name, e) => {
    let value = e.target.value;
    if (value === 'on') value = e.target.checked;
    const newDetails = [...model.listCustomers];
    const item = newDetails[index];

    if (name === 'customerCode') {
      const thTag = e.target.closest('.th');
      if (thTag) {
        const selecteTag = thTag.querySelector('.item_selected');
        if (value === '') item.name = '';
        if (selecteTag && selecteTag.innerText === value && value) {
          setIsLocked(true);
          item.name = '';
          try {
            const urlApi = `/api/users/getUserByCode?code=${value}`;
            const response = await axiosInstance.get(urlApi);

            const obj = response.data.data;
            Object.assign(item, obj);
          } catch (error) {
            const message = error.response ? error.response.data.message : error.message;
            console.error(error);
            toast.error(message);
          }
          setIsLocked(false);
        }
      }
    }

    item[name] = value;
    setModel({ ...model, listCustomers: newDetails });
  };

  return (
    <Modal show={show} size="xl" onEntered={() => handleModalShow()}>
      <div className="modal-dialog11 modal-lg11">
        <form id="product_editor_form" className="form_validate normal-form" autoComplete="off" onSubmit={handleSubmitForm}>
          <div className="modal-content">
            <div className="modal-header position-relative flex-between">
              {model.id && (<span className="record_id">ID: {model.id}</span>)}
              <div>
                <h5 className="modal-title">{getPageTitle(model.id, model.productType)}</h5>
                {model.error && (
                  <div className="error-msg-server">{model.error}</div>
                )}
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleModalClose}
                aria-label="Đóng"
              />
            </div>
            {model.isAdmin && model.productType === 'share' && (
              <ul className="nav nav-tabs mt-3">
                <li className="nav-item">
                  <div className="tab-mini"></div>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'tab1' ? 'active' : ''}`} onClick={() => setActiveTab('tab1')}>Thông tin chung</a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'tab2' ? 'active' : ''}`} onClick={() => handleActiveTab('tab2')}>Nhà cung cấp</a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'tab3' ? 'active' : ''}`} onClick={() => handleActiveTab('tab3')}>Khách hàng</a>
                </li>
              </ul>
            )}
            <div className="modal-body">
              <div data-name="tab1" className={`${activeTab === 'tab1' ? 'active' : 'd-none'}`}>
              <div className="row">
                {model.isAdmin && model.productType !== 'share' && (
                  <div className="col-md-3 mb-3">
                    <div>
                      <label className="form-label required">Mã khách hàng</label>
                      <div className="th filter-editor">
                        <span className="filter-ib" data-source-url={API_URL_All + '/api/users/searchUserCodes'}>
                          <input type="text" name="customerCode" placeholder="Nhập mã khách hàng..." data-required="Phải nhập mục này." disabled={isLocked} value={model.customerCode ?? ''} onChange={(e) => setModel({ ...model, customerCode: e.target.value })} />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-md-3 mb-3">
                  <label className="form-label required">Tên sản phẩm</label>
                  <div>
                    <input type="text" className="form-control" placeholder="Tên sản phẩm" data-required="Phải nhập mục này" disabled={isLocked} value={model.name ?? ''} onChange={(e) => setModel({ ...model, name: e.target.value })} />
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Mã sản phẩm mặc định</label>
                  <div>
                    <input type="text" className="form-control" placeholder="Mã sản phẩm" disabled={isLocked} value={model.code ?? ''} onChange={(e) => setModel({ ...model, code: e.target.value })} />
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                    <label className="form-label">Danh mục</label>
                    <div className="th select3 form-control p-0">
                      <span className="filter-ib w-100" data-filter="categoryNames" data-source-key="" data-source-url={API_URL_All + '/api/products/searchTreeCategoryNames'}><input type="text" disabled={isLocked} placeholder="Tên danh mục" /></span>
                    </div>
                  </div>
              </div>

                <div className="row">
                  <div className="col-md-3 mb-3">
                    <label className="form-label" data-note="vnđ">Giá nhập mặc định</label>
                    <div>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="vnđ"
                        disabled={isLocked}
                        value={groupMoney(model.price ?? '')}
                        onChange={(e) => setModel({ ...model, price: unformatMoney(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label" data-note="vnđ">Giá khai báo mặc định</label>
                    <div>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="vnđ"
                        disabled={isLocked}
                        value={groupMoney(model.priceDeclare ?? '')}
                        onChange={(e) => setModel({ ...model, priceDeclare: unformatMoney(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label" data-note="kg">Cân nặng mặc định</label>
                    <div>
                      <input type="number" className="form-control" placeholder="kg" disabled={isLocked} value={model.weight ?? ''} onChange={(e) => setModel({ ...model, weight: e.target.value })} />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <div className="size-options">
                      <ul>
                        <li className={model.size3 ? '' : 'active'} data-for="size">
                          <span className="form-label" data-note="m3">Thể tích mặc định</span>
                        </li>
                        <li data-for="size3" className={model.size3 ? 'active' : ''}>
                          <span className="form-label" data-note="dài x rộng x cao (cm)">Kích thước mặc định</span>
                        </li>
                      </ul>
                      <i className="fa-solid fa-caret-down"></i>
                    </div>
                    <label className="form-label">&nbsp;</label>
                    <div id="size" className={model.size3 ? 'd-none' : ''}>
                      <input type="number" name="size" className="form-control" placeholder="m3" disabled={isLocked} value={model.size ?? ''} onChange={(e) => setModel({ ...model, size: e.target.value })} />
                    </div>
                    <div id="size3" className={model.size3 ? 'size-3' : 'size-3 d-none'}>
                      <input type="number" className="form-control" placeholder="dài cm" disabled={isLocked} value={model.sizeX ?? ''} onChange={(e) => setModel({ ...model, sizeX: e.target.value })} />
                      <input type="number" className="form-control" placeholder="rộng cm" disabled={isLocked} value={model.sizeY ?? ''} onChange={(e) => setModel({ ...model, sizeY: e.target.value })} />
                      <input type="number" className="form-control" placeholder="cao cm" disabled={isLocked} value={model.sizeZ ?? ''} onChange={(e) => setModel({ ...model, sizeZ: e.target.value })} />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="img-preview ratio ratio-1x1">
                      <img src={model.imagePath ? `${API_URL_All}/${model.imagePath}` : ''} />
                      <input type="file" className="d-none" disabled={isLocked} />
                    </div>
                  </div>
                  <div className="col-md-8 mb-3">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Mô tả</label>
                      <div>
                        <textarea rows={5} type="text" className="form-control" placeholder="Mô tả" disabled={isLocked} value={model.description ?? ''} onChange={(e) => setModel({ ...model, description: e.target.value })}></textarea>
                      </div>
                    </div>
                    <div className="group-box mb-2" data-text="Thông tin nhập khẩu mặc định">
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Mã HS</label>
                          <div>
                            <input type="text" className="form-control" placeholder="Mã HS" disabled={isLocked} value={model.taxCode ?? ''} onChange={(e) => setModel({ ...model, taxCode: e.target.value })} />
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label" data-note="%">Thuế nhập khẩu</label>
                          <div>
                            <input type="number" className="form-control" placeholder="Thuế nhập khẩu" disabled={isLocked} value={model.taxImport ?? ''} onChange={(e) => setModel({ ...model, taxImport: e.target.value })} />
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label" data-note="%">VAT</label>
                          <div>
                            <input type="number" className="form-control" placeholder="VAT" disabled={isLocked} value={model.taxVat ?? ''} onChange={(e) => setModel({ ...model, taxVat: e.target.value })} />
                          </div>
                        </div>
                      </div>
                    </div>
                    {model.isAdmin && (
                      <div className="row">
                        <div className="col-md-12 mb-3">
                          <label className="form-label">Ghi chú</label>
                          <div>
                            <textarea rows={3} type="text" className="form-control" placeholder="Ghi chú" disabled={isLocked} value={model.note ?? ''} onChange={(e) => setModel({ ...model, note: e.target.value })}></textarea>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12 mb-3">
                    <div className="group-box" data-text="Thuộc tính">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="product-variant-switch"
                              disabled={isLocked}
                              checked={hasVariants}
                              onChange={(e) => handleVariantToggle(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="product-variant-switch">
                              Thêm thuộc tính riêng cho từng sản phẩm con
                            </label>
                          </div>
                          {!hasVariants && (
                            <small>
                              Nếu không có thuộc tính nào thì sẽ lấy thông tin mặc định của sản phẩm
                            </small>
                          )}
                        </div>
                      </div>

                      {hasVariants && (
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-bordered align-middle">
                              <thead>
                                <tr>
                                  <th className="text-center" style={{ width: '1%' }}>Ảnh sản phẩm</th>
                                  <th style={{ minWidth: '180px' }}>Tên thuộc tính</th>
                                  <th style={{ minWidth: '140px' }}>Mã sản phẩm</th>
                                  <th style={{ minWidth: '120px' }}>Giá nhập</th>
                                  <th style={{ minWidth: '120px' }}>Giá khai</th>
                                  <th style={{ minWidth: '110px' }}>Khối lượng</th>
                                  <th style={{ minWidth: '220px' }}>
                                    <div className="d-flex flex-column align-items-center">
                                      <select
                                        className="form-label bg-transparent border-0 m-0"
                                        disabled={isLocked}
                                        value={variantSizeMode}
                                        onChange={(e) => handleVariantSizeModeChange(e.target.value)}
                                      >
                                        <option value="size">Thể tích (m3)</option>
                                        <option value="size3">Kích thước (cm)</option>
                                      </select>
                                    </div>
                                  </th>
                                  <th style={{ minWidth: '140px' }}>Mã HS</th>
                                  <th style={{ minWidth: '120px' }}>Thuế NK</th>
                                  <th style={{ minWidth: '100px' }}>VAT</th>
                                  <th style={{ minWidth: '220px' }}>Mô tả</th>
                                  <th style={{ minWidth: '180px' }}>Ghi chú</th>
                                  {!isLocked && <th style={{ width: '72px' }}></th>}
                                </tr>
                              </thead>
                              <tbody>
                                {properties.map((property, index) => (
                                  <tr key={property.id || `variant-${index}`}>
                                    <td>
                                      <div className="d-flex justify-content-center">
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="d-none"
                                          disabled={isLocked}
                                          ref={(element) => {
                                            if (element) {
                                              variantImageInputRefs.current[index] = element;
                                            } else {
                                              delete variantImageInputRefs.current[index];
                                            }
                                          }}
                                          onChange={(e) => setVariantImage(index, e)}
                                        />
                                        <div
                                          role={!isLocked ? 'button' : undefined}
                                          tabIndex={!isLocked ? 0 : undefined}
                                          className="position-relative"
                                          onClick={() => openVariantImagePicker(index)}
                                          onKeyDown={(e) => {
                                            if (isLocked) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              openVariantImagePicker(index);
                                            }
                                          }}
                                          style={{ cursor: isLocked ? 'default' : 'pointer' }}
                                        >
                                          {(property.imagePreviewUrl || property.imagePath) ? (
                                            <img
                                              src={property.imagePreviewUrl || `${API_URL_All}/${property.imagePath}`}
                                              alt={property.propertiesValue || 'variant'}
                                              style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #dee2e6' }}
                                            />
                                          ) : (
                                            <div
                                              className="d-flex align-items-center justify-content-center text-center text-muted small"
                                              style={{ width: '72px', height: '72px', borderRadius: '8px', border: '1px dashed #ced4da', padding: '6px', background: '#f8f9fa' }}
                                            >
                                              Chua co anh
                                            </div>
                                          )}
                                          {!isLocked && (property.imagePreviewUrl || property.imagePath) && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-light border-0 shadow-sm position-absolute top-0 end-0 d-flex align-items-center justify-content-center"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                clearVariantImage(index);
                                              }}
                                              style={{ width: '22px', height: '22px', borderRadius: '50%', transform: 'translate(30%, -30%)', padding: 0 }}
                                              aria-label="Xoa anh"
                                            >
                                              <RiDeleteBin6Line size={12} />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ví dụ: Size M / Màu đỏ"
                                        disabled={isLocked}
                                        value={property.propertiesValue ?? ''}
                                        onChange={(e) => setVariantItem(index, 'propertiesValue', e)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="SKU / mã riêng"
                                        disabled={isLocked}
                                        value={property.code ?? ''}
                                        onChange={(e) => setVariantItem(index, 'code', e)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        className="form-control"
                                        placeholder="vnđ"
                                        disabled={isLocked}
                                        value={groupMoney(property.price ?? '')}
                                        onChange={(e) => setVariantItem(index, 'price', { target: { value: unformatMoney(e.target.value) } })}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        className="form-control"
                                        placeholder="vnđ"
                                        disabled={isLocked}
                                        value={groupMoney(property.priceDeclare ?? '')}
                                        onChange={(e) => setVariantItem(index, 'priceDeclare', { target: { value: unformatMoney(e.target.value) } })}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="kg"
                                        disabled={isLocked}
                                        value={property.weight ?? ''}
                                        onChange={(e) => setVariantItem(index, 'weight', e)}
                                      />
                                    </td>
                                    <td>
                                      {variantSizeMode !== 'size3' && (
                                        <input
                                          type="number"
                                          className="form-control"
                                          placeholder="m3"
                                          disabled={isLocked}
                                          value={property.size ?? ''}
                                          onChange={(e) => setVariantItem(index, 'size', e)}
                                        />
                                      )}
                                      {variantSizeMode === 'size3' && (
                                        <div className="size-3">
                                          <input type="number" className="form-control" placeholder="dài" disabled={isLocked} value={property.sizeX ?? ''} onChange={(e) => setVariantItem(index, 'sizeX', e)} />
                                          <input type="number" className="form-control" placeholder="rộng" disabled={isLocked} value={property.sizeY ?? ''} onChange={(e) => setVariantItem(index, 'sizeY', e)} />
                                          <input type="number" className="form-control" placeholder="cao" disabled={isLocked} value={property.sizeZ ?? ''} onChange={(e) => setVariantItem(index, 'sizeZ', e)} />
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Mã HS"
                                        disabled={isLocked}
                                        value={property.taxCode ?? ''}
                                        onChange={(e) => setVariantItem(index, 'taxCode', e)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="%"
                                        disabled={isLocked}
                                        value={property.taxImport ?? ''}
                                        onChange={(e) => setVariantItem(index, 'taxImport', e)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="%"
                                        disabled={isLocked}
                                        value={property.taxVat ?? ''}
                                        onChange={(e) => setVariantItem(index, 'taxVat', e)}
                                      />
                                    </td>
                                    <td>
                                      <textarea
                                        className="form-control"
                                        rows="2"
                                        placeholder="Mô tả riêng của biến thể"
                                        disabled={isLocked}
                                        value={property.description ?? ''}
                                        onChange={(e) => setVariantItem(index, 'description', e)}
                                      />
                                    </td>
                                    <td>
                                      <textarea
                                        className="form-control"
                                        rows="2"
                                        placeholder="Ghi chú riêng"
                                        disabled={isLocked}
                                        value={property.note ?? ''}
                                        onChange={(e) => setVariantItem(index, 'note', e)}
                                      />
                                    </td>
                                    {!isLocked && (
                                      <td className="text-center">
                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeVariant(index)}>
                                          <RiDeleteBin6Line />
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {!isLocked && (
                            <div className="d-flex justify-content-end mt-2">
                              <button type="button" className="small-btn d-inline-flex align-items-center gap-1" onClick={addVariant}>
                                <FaPlus /> Thêm thuộc tính
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <ProductDrivePanel productId={model.id} readOnly={isLocked} />

                <div className="row mt-2">
                  <div className="col-md-12">
                    <label className="form-label">Người phụ trách</label>
                    {canEditCarePerson ? (
                      <select
                        name="careId"
                        className="form-control"
                        value={model.careId ?? ""}
                        onChange={(e) => {
                          const nextId = e.target.value ? Number(e.target.value) : "";
                          const picked = adminUsers.find((u) => Number(u.id) === Number(nextId));
                          setModel({
                            ...model,
                            careId: nextId,
                            careName: picked
                              ? (picked.name || picked.username || picked.email || "")
                              : "",
                          });
                        }}
                      >
                        <option value="">— Chọn người phụ trách —</option>
                        {model.careId &&
                          !adminUsers.some((u) => Number(u.id) === Number(model.careId)) && (
                            <option value={model.careId}>
                              {model.careName || `User #${model.careId}`}
                            </option>
                          )}
                        {adminUsers.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name || u.username || u.email || `User #${u.id}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        readOnly
                        value={carePersonDisplayName}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div data-name="tab2" className={`${activeTab === 'tab2' ? 'active' : 'd-none'}`}>
                <div className="group-box" data-text="Lịch sử đặt hàng">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="table-normal">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th width="40px">No</th>
                              <th width="60px">OrderId</th>
                              <th>NCC</th>
                              <th><span data-note="CNY">Giá NCC</span></th>
                              <th>Ngày</th>
                            </tr>
                          </thead>
                          <tbody>
                            {model.listOrders && model.listOrders.map((item, index) => (
                              <tr key={`${item.orderId}-${index}`}>
                                <td>{index + 1}</td>
                                <td className="text-start">{item.orderId}</td>
                                <td className="text-start">{item.providerName}</td>
                                <td className="text-end">{general.formatNumber(item.priceIn)}</td>
                                <td>{general.formatDate(item.orderDate)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tbody>
                            {model.listOrders && model.listOrders.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center"><small className="fst-italic">Chưa có đơn hàng nào</small></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                {model.listProviders && model.listProviders.map((item, index) => (
                  <div className="row" key={`provider-${index}`}>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Nhà cung cấp {index + 1}</label>
                      <div className="th filter-editor">
                        <span className="filter-ib" data-source-url={API_URL_All + '/api/providers/searchProviderNames'}><input type="text" name={`listProviders[${index}].providerName`} disabled={isLocked} placeholder="Nhập tên nhà cung cấp..." value={item.providerName ?? ''} onChange={(e) => setDetailsItem(index, 'providerName', e)} onBlur={() => resolveProviderOnBlur(index)} /></span>
                      </div>
                    </div>
                    <div className="col-md-7 mb-3">
                      <label className="form-label">Link sản phẩm</label>
                      <div>
                        <input type="text" className="form-control" placeholder="Link sản phẩm" disabled={isLocked} value={item.productLink ?? ''} onChange={(e) => setDetailsItem(index, 'productLink', e)} />
                      </div>
                    </div>
                    <div className="col-md-2 mt-2">
                      <div>
                        {item.providerId && (<div className="flex-between"><span>SP:</span><span><StarRating rating={item.starProduct} /></span></div>)}
                        {item.providerId && (<div className="flex-between"><span>Giá:</span><span><StarRating rating={item.starPrice} /></span></div>)}
                        {item.providerId && (<div className="flex-between"><span>Giao:</span><span><StarRating rating={item.starProcess} /></span></div>)}
                      </div>
                    </div>
                  </div>
                ))}
                {!isLocked && (
                  <button type="button" className="small-btn d-inline-flex align-items-center gap-1" onClick={addProvider}>
                    <FaPlus /> Thêm nhà cung cấp
                  </button>
                )}
              </div>

              <div data-name="tab3" className={`${activeTab === 'tab3' ? 'active' : 'd-none'}`}>
                {model.listCustomers && model.listCustomers.map((item, index) => (
                  <div className="row mt-2" key={`customer-${index}`}>
                    <label className="form-label col-form-label col-sm-2 col-4">Khách hàng {index + 1}</label>
                    <div className="col-sm-5 col-4">
                      <div className="th filter-editor">
                        <span className="filter-ib" data-source-url={API_URL_All + '/api/users/searchUserCodes'}><input type="text" name={`listCustomers[${index}].customerCode`} disabled={isLocked} placeholder="Nhập mã khách hàng..." value={item.customerCode ?? ''} onChange={(e) => setUserItem(index, 'customerCode', e)} /></span>
                      </div>
                    </div>
                    <div className="col-sm-5 col-4"><input name="name" placeholder="Tên khách hàng" type="text" disabled className="form-control" value={item.name ?? ""} /></div>
                  </div>
                ))}

                <div className="row">
                  <div className="col-12">
                    {!isLocked && (
                      <button type="button" className="small-btn d-inline-flex align-items-center gap-1" onClick={addCustomer}>
                        <FaPlus /> Thêm khách hàng
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer justify-content-between">
              <div>
                {model.id && effectivePerm.canDelete && (
                  <button type="button" className="btn-small d-inline-flex align-items-center gap-1" onClick={() => handleDeleteProduct()}>
                    <RiDeleteBin6Line /> Xóa
                  </button>
                )}
              </div>
              {!isLocked ? (
                <button type="submit" className="btn-sm btn btn-primary d-inline-flex align-items-center gap-1">
                  <FiSave /> Lưu sản phẩm
                </button>
              ) : (
                <button type="button" className="btn-sm btn btn-secondary d-inline-flex align-items-center gap-1" onClick={handleModalClose}>
                  <FiX /> Đóng
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ProductEditor;
